import type { Cell, Workbook, Worksheet } from 'exceljs';
import { getExcelTemplateFilename } from '../excel-template-filename';
import { getListQuestionDescriptors, type ListQuestionDescriptor } from '../list-question-utils';
import {
  getListPageExcelColumnDependency,
  getListPageExcelColumnFieldName,
  getListPageExcelColumns,
  getListPageExcelColumnTitle,
  type ListPageExcelColumn,
} from './columns';
import {
  assertListPageExcelSupported,
  buildListPageReplacementAnswers,
  getListPageEditorRows,
  getListPageExcelRequirement,
  type ListPageExcelOptions,
} from './editor-model';
import { getListPageExcelTemplateSignature } from './template';
import {
  getCellText,
  type ListPageExcelValidationError,
  validateListPageExcelRows,
} from './validation';

export const LIST_EXCEL_TITLE_ROW = 1;
export const LIST_EXCEL_HEADER_ROW = 2;
export const LIST_EXCEL_REQUIREMENT_ROW = 3;
export const LIST_EXCEL_FIELD_NAME_ROW = 4;
export const LIST_EXCEL_FIRST_DATA_ROW = 5;
export const LIST_EXCEL_TEMPLATE_ROWS = 50;

const OPTIONS_WORKSHEET_NAME = '__Lists';
const ERRORS_WORKSHEET_NAME = 'Errors';
const HEADER_FILL = 'D9EAF7';
const REQUIREMENT_FILL = 'F3F2F1';
const ERROR_FILL = 'FFFFB3B3';
const ERROR_FONT = 'FF8A0000';
const ERROR_NOTE_PREFIX = 'GRID Forms validation:';
const BORDER_COLOUR = 'B1B4B6';
const EMPTY_OPTIONS_RANGE_NAME = 'GRID_LIST_EMPTY';

export type { ListPageExcelOptions } from './editor-model';

export type ListPageExcelImportResult =
  | {
      status: 'valid';
      answers: Record<string, unknown>;
      responseCount: number;
      rows?: unknown[][];
    }
  | {
      status: 'invalid-template';
    }
  | {
      status: 'invalid';
      message: string;
      errorCount: number;
      errorBuffer: ArrayBuffer;
      errorFileName: string;
      rows?: unknown[][];
      errors?: ListPageExcelValidationError[];
    };

const getSafeName = (title: string) =>
  title
    .replace(/[\\/?*[\]:]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getWorksheetName = (title: string) => getSafeName(title).slice(0, 31) || 'Responses';

const getFileStem = (title: string) =>
  title
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '') || 'list-responses';

const getColumnLetter = (columnNumber: number) => {
  let number = columnNumber;
  let result = '';
  while (number > 0) {
    number -= 1;
    result = String.fromCharCode(65 + (number % 26)) + result;
    number = Math.floor(number / 26);
  }
  return result;
};

const getOptionsRangeName = (descriptorIndex: number) => `GRID_LIST_OPTIONS_${descriptorIndex + 1}`;
const getDependencyRangeName = (columnIndex: number) => `GRID_LIST_DEPENDENCY_${columnIndex + 1}`;

const applyCellBorder = (cell: Cell) => {
  cell.border = {
    top: { style: 'thin', color: { argb: BORDER_COLOUR } },
    left: { style: 'thin', color: { argb: BORDER_COLOUR } },
    bottom: { style: 'thin', color: { argb: BORDER_COLOUR } },
    right: { style: 'thin', color: { argb: BORDER_COLOUR } },
  };
};

const getWorksheetCellValue = (value: unknown) => {
  if (
    value === null ||
    value === undefined ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value ?? null;
  }
  return String(value);
};

const addOptionWorksheet = (
  workbook: Workbook,
  descriptors: ListQuestionDescriptor[],
  columns: ListPageExcelColumn[],
) => {
  const worksheet = workbook.addWorksheet(OPTIONS_WORKSHEET_NAME, { state: 'veryHidden' });
  descriptors.forEach((descriptor, descriptorIndex) => {
    const optionsColumn = getColumnLetter(descriptorIndex + 1);
    descriptor.options.forEach((option, optionIndex) => {
      worksheet.getCell(optionIndex + 1, descriptorIndex + 1).value = option.label;
    });
    if (descriptor.options.length > 0) {
      workbook.definedNames.add(
        `${OPTIONS_WORKSHEET_NAME}!$${optionsColumn}$1:` +
          `$${optionsColumn}$${descriptor.options.length}`,
        getOptionsRangeName(descriptorIndex),
      );
    }
  });

  const emptyOptionsColumnIndex = descriptors.length + 1;
  const emptyOptionsColumn = getColumnLetter(emptyOptionsColumnIndex);
  workbook.definedNames.add(
    `${OPTIONS_WORKSHEET_NAME}!$${emptyOptionsColumn}$1`,
    EMPTY_OPTIONS_RANGE_NAME,
  );

  let dependencyColumnIndex = emptyOptionsColumnIndex + 1;
  columns.forEach((column, columnIndex) => {
    if (!getListPageExcelColumnDependency(column)) return;
    const firstMapColumn = getColumnLetter(dependencyColumnIndex);
    const secondMapColumn = getColumnLetter(dependencyColumnIndex + 1);
    column.descriptors.forEach((descriptor, optionIndex) => {
      const condition = descriptor.conditions.at(-1);
      const descriptorIndex = descriptors.indexOf(descriptor);
      worksheet.getCell(optionIndex + 1, dependencyColumnIndex).value = condition?.optionLabel;
      worksheet.getCell(optionIndex + 1, dependencyColumnIndex + 1).value =
        getOptionsRangeName(descriptorIndex);
    });
    workbook.definedNames.add(
      `${OPTIONS_WORKSHEET_NAME}!$${firstMapColumn}$1:` +
        `$${secondMapColumn}$${column.descriptors.length}`,
      getDependencyRangeName(columnIndex),
    );
    dependencyColumnIndex += 2;
  });
  return worksheet;
};

const applyDataValidation = (
  cell: Cell,
  column: ListPageExcelColumn,
  columnIndex: number,
  descriptors: ListQuestionDescriptor[],
  columns: ListPageExcelColumn[],
) => {
  const descriptor = column.descriptors[0];
  const required = column.descriptors.some(({ question }) => question.rules?.required === true);
  const allowBlank = !required || descriptor.conditions.length > 0;
  if (descriptor.kind === 'list') {
    const dependency = getListPageExcelColumnDependency(column);
    const descriptorIndex = descriptors.indexOf(descriptor);
    let validationFormula = getOptionsRangeName(descriptorIndex);
    if (dependency) {
      const dependencyColumnIndex = columns.findIndex((candidate) =>
        candidate.descriptors.some(
          ({ question }) => String(question.name) === dependency.questionName,
        ),
      );
      if (dependencyColumnIndex < 0) {
        throw new Error(`Excel dependency field ${dependency.questionName} was not found.`);
      }
      const dependencyCell = `$${getColumnLetter(dependencyColumnIndex + 1)}${cell.row}`;
      validationFormula =
        `INDIRECT(IFERROR(VLOOKUP(${dependencyCell},` +
        `${getDependencyRangeName(columnIndex)},2,FALSE),"${EMPTY_OPTIONS_RANGE_NAME}"))`;
    }
    cell.dataValidation = {
      type: 'list',
      allowBlank,
      formulae: [validationFormula],
      showErrorMessage: true,
      errorStyle: 'error',
      errorTitle: 'Select a valid option',
      error: 'Choose a value from the dropdown list.',
      showInputMessage: true,
      promptTitle: descriptor.question.title.slice(0, 32),
      prompt:
        required && descriptor.conditions.length === 0
          ? 'This field is required.'
          : 'Choose a value or leave blank.',
    };
    return;
  }

  if (descriptor.kind === 'number') {
    const isDecimal = Boolean(
      descriptor.question.rules?.decimal || descriptor.question.rules?.float,
    );
    const isNonNegativeInput =
      descriptor.question.questionType === 'instanceInput' ||
      descriptor.question.questionType === 'costInput';
    cell.dataValidation = {
      type: isDecimal ? 'decimal' : 'whole',
      operator: 'between',
      allowBlank,
      formulae: [
        descriptor.question.rules?.min ?? (isNonNegativeInput ? 0 : -999999999999),
        descriptor.question.rules?.max ?? 999999999999,
      ],
      showErrorMessage: true,
      errorStyle: 'error',
      errorTitle: isDecimal ? 'Enter a number' : 'Enter a whole number',
      error: isDecimal ? 'Enter a valid number.' : 'Enter a valid whole number.',
    };
    cell.numFmt = isDecimal ? '0.00' : '0';
    return;
  }

  if (descriptor.kind === 'text') {
    const maxLength =
      descriptor.question.characterCount || descriptor.question.rules?.maxLength || undefined;
    if (maxLength) {
      cell.dataValidation = {
        type: 'textLength',
        operator: 'lessThanOrEqual',
        allowBlank,
        formulae: [maxLength],
        showErrorMessage: true,
        errorStyle: 'error',
        errorTitle: 'Text is too long',
        error: `Enter ${maxLength} characters or fewer.`,
      };
    }
  }
};

const addMainWorksheet = (
  workbook: Workbook,
  options: ListPageExcelOptions,
  descriptors: ListQuestionDescriptor[],
  columns: ListPageExcelColumn[],
  editorRows?: unknown[][],
) => {
  const rows = editorRows ?? getListPageEditorRows(options, { descriptors, columns });
  const dataRowCount = Math.max(LIST_EXCEL_TEMPLATE_ROWS, rows.length);
  const worksheet = workbook.addWorksheet(getWorksheetName(options.title), {
    views: [
      {
        state: 'frozen',
        ySplit: LIST_EXCEL_FIELD_NAME_ROW,
        topLeftCell: `A${LIST_EXCEL_FIRST_DATA_ROW}`,
        activeCell: `A${LIST_EXCEL_FIRST_DATA_ROW}`,
      },
    ],
  });

  worksheet.mergeCells(LIST_EXCEL_TITLE_ROW, 1, LIST_EXCEL_TITLE_ROW, Math.max(columns.length, 1));
  const titleCell = worksheet.getCell(LIST_EXCEL_TITLE_ROW, 1);
  titleCell.value = options.title;
  titleCell.font = { bold: true, size: 16 };
  titleCell.alignment = { vertical: 'middle' };
  worksheet.getRow(LIST_EXCEL_TITLE_ROW).height = 28;

  columns.forEach((column, descriptorIndex) => {
    const descriptor = column.descriptors[0];
    const columnIndex = descriptorIndex + 1;
    const headerCell = worksheet.getCell(LIST_EXCEL_HEADER_ROW, columnIndex);
    headerCell.value = getListPageExcelColumnTitle(column);
    headerCell.font = { bold: true };
    headerCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_FILL } };
    headerCell.alignment = { vertical: 'top', wrapText: true };
    applyCellBorder(headerCell);

    const requirementCell = worksheet.getCell(LIST_EXCEL_REQUIREMENT_ROW, columnIndex);
    requirementCell.value = getListPageExcelRequirement(column);
    requirementCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: REQUIREMENT_FILL },
    };
    requirementCell.alignment = { vertical: 'top', wrapText: true };
    applyCellBorder(requirementCell);

    worksheet.getCell(LIST_EXCEL_FIELD_NAME_ROW, columnIndex).value =
      getListPageExcelColumnFieldName(column);
    worksheet.getColumn(columnIndex).width = Math.min(
      55,
      Math.max(20, getListPageExcelColumnTitle(column).length / 1.5),
    );

    for (let rowIndex = 0; rowIndex < dataRowCount; rowIndex += 1) {
      const cell = worksheet.getCell(LIST_EXCEL_FIRST_DATA_ROW + rowIndex, columnIndex);
      cell.value = getWorksheetCellValue(rows[rowIndex]?.[descriptorIndex]);
      cell.alignment = { vertical: 'top', wrapText: true };
      applyCellBorder(cell);
      applyDataValidation(cell, column, descriptorIndex, descriptors, columns);
    }
  });

  worksheet.getRow(LIST_EXCEL_FIELD_NAME_ROW).hidden = true;
  worksheet.getRow(LIST_EXCEL_HEADER_ROW).height = 44;
  worksheet.getRow(LIST_EXCEL_REQUIREMENT_ROW).height = 64;
  worksheet.autoFilter = {
    from: { row: LIST_EXCEL_HEADER_ROW, column: 1 },
    to: { row: LIST_EXCEL_HEADER_ROW, column: columns.length },
  };
  return worksheet;
};

export const buildListPageWorkbook = async (
  options: ListPageExcelOptions,
  editorRows?: unknown[][],
) => {
  const descriptors = getListQuestionDescriptors(options.questions);
  const columns = getListPageExcelColumns(descriptors);
  assertListPageExcelSupported(descriptors);
  if (descriptors.length === 0) {
    throw new Error('This list page does not contain any fields that can be exported to Excel.');
  }

  const { Workbook: ExcelWorkbook } = await import('exceljs');
  const workbook = new ExcelWorkbook();
  workbook.creator = 'GRID Forms';
  workbook.created = new Date();
  workbook.title = options.title;
  workbook.subject = options.pageName;
  workbook.keywords = getListPageExcelTemplateSignature({
    tenant: options.tenant,
    pageName: options.pageName,
    descriptors,
  });
  addMainWorksheet(workbook, options, descriptors, columns, editorRows);
  addOptionWorksheet(workbook, descriptors, columns);
  return workbook;
};

export const downloadExcelBuffer = (buffer: ArrayBuffer, fileName: string) => {
  const url = URL.createObjectURL(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
  );
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
};

export const downloadListPageWorkbook = async (
  options: ListPageExcelOptions,
  editorRows?: unknown[][],
) => {
  const workbook = await buildListPageWorkbook(options, editorRows);
  const buffer = await workbook.xlsx.writeBuffer();
  downloadExcelBuffer(buffer as ArrayBuffer, getExcelTemplateFilename(options.title));
};

const isBlankValue = (value: unknown) => value === null || value === undefined || value === '';

const clearPreviousErrorMarkers = (worksheet: Worksheet) => {
  worksheet.eachRow({ includeEmpty: false }, (row) => {
    row.eachCell({ includeEmpty: false }, (cell) => {
      const noteText =
        typeof cell.note === 'string'
          ? cell.note
          : cell.note?.texts?.map(({ text }) => text).join('') || '';
      if (!noteText.startsWith(ERROR_NOTE_PREFIX)) return;
      cell.note = '';
      const style = { ...cell.style };
      if (
        cell.fill.type === 'pattern' &&
        cell.fill.pattern === 'solid' &&
        cell.fill.fgColor?.argb === ERROR_FILL
      ) {
        style.fill = { type: 'pattern', pattern: 'none' };
      }
      if (cell.font?.color?.argb === ERROR_FONT) {
        style.font = { ...cell.font, color: undefined };
      }
      cell.style = style;
    });
  });
};

const addError = (
  errors: ListPageExcelValidationError[],
  worksheetRow: number,
  columnIndex: number,
  column: ListPageExcelColumn | undefined,
  message: string,
) => {
  errors.push({
    worksheetRow,
    columnIndex,
    fieldName: column ? getListPageExcelColumnFieldName(column) : '',
    fieldTitle: column ? getListPageExcelColumnTitle(column) : 'Workbook layout',
    message,
  });
};

const validateWorkbookLayout = (
  worksheet: Worksheet,
  options: ListPageExcelOptions,
  columns: ListPageExcelColumn[],
) => {
  const errors: ListPageExcelValidationError[] = [];
  const readLayoutCellText = (
    worksheetRow: number,
    columnIndex: number,
    column?: ListPageExcelColumn,
  ) => {
    try {
      return getCellText(worksheet.getCell(worksheetRow, columnIndex).value);
    } catch (error) {
      addError(
        errors,
        worksheetRow,
        columnIndex,
        column,
        error instanceof Error ? error.message : 'This template cell cannot be read.',
      );
      return undefined;
    }
  };

  const actualPageTitle = readLayoutCellText(LIST_EXCEL_TITLE_ROW, 1);
  if (actualPageTitle !== undefined && actualPageTitle !== options.title) {
    addError(
      errors,
      LIST_EXCEL_TITLE_ROW,
      1,
      undefined,
      'Use the downloaded template without changing the page title.',
    );
  }

  columns.forEach((column, descriptorIndex) => {
    const columnIndex = descriptorIndex + 1;
    const expectedTitle = getListPageExcelColumnTitle(column);
    const actualTitle = readLayoutCellText(LIST_EXCEL_HEADER_ROW, columnIndex, column);
    if (actualTitle !== undefined && actualTitle !== expectedTitle) {
      addError(
        errors,
        LIST_EXCEL_HEADER_ROW,
        columnIndex,
        column,
        `Expected column title "${expectedTitle}".`,
      );
    }
    const actualFieldName = readLayoutCellText(LIST_EXCEL_FIELD_NAME_ROW, columnIndex, column);
    if (
      actualFieldName !== undefined &&
      actualFieldName !== getListPageExcelColumnFieldName(column)
    ) {
      addError(
        errors,
        LIST_EXCEL_FIELD_NAME_ROW,
        columnIndex,
        column,
        'Use the downloaded template without changing or deleting columns.',
      );
    }
  });

  const firstUnexpectedColumn = columns.length + 1;
  const unexpectedTitle = readLayoutCellText(LIST_EXCEL_HEADER_ROW, firstUnexpectedColumn);
  const unexpectedFieldName = readLayoutCellText(LIST_EXCEL_FIELD_NAME_ROW, firstUnexpectedColumn);
  if (unexpectedTitle || unexpectedFieldName) {
    addError(
      errors,
      LIST_EXCEL_HEADER_ROW,
      firstUnexpectedColumn,
      undefined,
      'Remove columns that were not in the downloaded template.',
    );
  }
  return errors;
};

const annotateErrors = (
  workbook: Workbook,
  worksheet: Worksheet,
  errors: ListPageExcelValidationError[],
) => {
  const existingErrorsSheet = workbook.getWorksheet(ERRORS_WORKSHEET_NAME);
  if (existingErrorsSheet) workbook.removeWorksheet(existingErrorsSheet.id);

  const errorsByCell = new Map<string, string[]>();
  errors.forEach((error) => {
    const key = `${error.worksheetRow}:${error.columnIndex}`;
    const messages = errorsByCell.get(key) || [];
    if (!messages.includes(error.message)) messages.push(error.message);
    errorsByCell.set(key, messages);
  });

  errorsByCell.forEach((messages, key) => {
    const [rowNumber, columnNumber] = key.split(':').map(Number);
    const cell = worksheet.getCell(rowNumber, columnNumber);
    cell.style = {
      ...cell.style,
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: ERROR_FILL } },
      font: { ...cell.font, color: { argb: ERROR_FONT } },
    };
    cell.note = `${ERROR_NOTE_PREFIX}\n${messages.join('\n')}`;
  });

  const errorsWorksheet = workbook.addWorksheet(ERRORS_WORKSHEET_NAME, {
    views: [{ state: 'frozen', ySplit: 1 }],
  });
  errorsWorksheet.addRow(['Worksheet', 'Row', 'Column', 'Field', 'Error']);
  errors.forEach((error) => {
    errorsWorksheet.addRow([
      worksheet.name,
      error.worksheetRow,
      getColumnLetter(error.columnIndex),
      error.fieldTitle,
      error.message,
    ]);
  });
  errorsWorksheet.getRow(1).font = { bold: true };
  errorsWorksheet.getRow(1).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: HEADER_FILL },
  };
  errorsWorksheet.columns = [
    { width: 30 },
    { width: 10 },
    { width: 10 },
    { width: 45 },
    { width: 80 },
  ];
};

const buildInvalidResult = async (
  workbook: Workbook,
  worksheet: Worksheet,
  errors: ListPageExcelValidationError[],
  title: string,
  rows: unknown[][],
): Promise<ListPageExcelImportResult> => {
  annotateErrors(workbook, worksheet, errors);
  const errorBuffer = (await workbook.xlsx.writeBuffer()) as ArrayBuffer;
  return {
    status: 'invalid',
    message: `${errors.length} validation error${errors.length === 1 ? '' : 's'} found. Download the marked file to see what must be fixed.`,
    errorCount: errors.length,
    errorBuffer,
    errorFileName: `${getFileStem(title)}-errors.xlsx`,
    rows,
    errors,
  };
};

export const importListPageWorkbook = async (
  file: File,
  options: ListPageExcelOptions,
): Promise<ListPageExcelImportResult> => {
  if (!file.name.toLowerCase().endsWith('.xlsx')) {
    throw new Error('Select an XLSX file.');
  }
  const descriptors = getListQuestionDescriptors(options.questions);
  const columns = getListPageExcelColumns(descriptors);
  assertListPageExcelSupported(descriptors);

  const { Workbook: ExcelWorkbook } = await import('exceljs');
  const workbook = new ExcelWorkbook();
  try {
    await workbook.xlsx.load(await file.arrayBuffer());
  } catch (_error) {
    throw new Error('The XLSX file could not be opened.');
  }

  const expectedTemplateSignature = getListPageExcelTemplateSignature({
    tenant: options.tenant,
    pageName: options.pageName,
    descriptors,
  });
  if (workbook.keywords !== expectedTemplateSignature) {
    return { status: 'invalid-template' };
  }

  const expectedWorksheetName = getWorksheetName(options.title);
  const worksheet = workbook.getWorksheet(expectedWorksheetName);
  if (!worksheet) {
    return { status: 'invalid-template' };
  }
  clearPreviousErrorMarkers(worksheet);

  const errors = validateWorkbookLayout(worksheet, options, columns);
  if (errors.length > 0) {
    return { status: 'invalid-template' };
  }
  const lastRow = Math.max(
    worksheet.rowCount,
    LIST_EXCEL_FIRST_DATA_ROW + LIST_EXCEL_TEMPLATE_ROWS - 1,
  );
  const rawRows: Array<{ worksheetRow: number; values: unknown[] }> = [];
  for (let worksheetRow = LIST_EXCEL_FIRST_DATA_ROW; worksheetRow <= lastRow; worksheetRow += 1) {
    const values = columns.map(
      (_column, columnIndex) => worksheet.getCell(worksheetRow, columnIndex + 1).value,
    );
    if (values.every(isBlankValue)) continue;
    rawRows.push({ worksheetRow, values });
  }

  const validation = validateListPageExcelRows({
    rows: rawRows,
    descriptors,
    tenant: options.tenant,
  });
  errors.push(...validation.errors);
  if (rawRows.length === 0) {
    addError(
      errors,
      LIST_EXCEL_FIRST_DATA_ROW,
      1,
      columns[0],
      'Enter at least one response before uploading this file.',
    );
  }

  if (errors.length > 0) {
    return buildInvalidResult(
      workbook,
      worksheet,
      errors,
      options.title,
      rawRows.map(({ values }) => values),
    );
  }

  return {
    status: 'valid',
    answers: buildListPageReplacementAnswers(options, validation.rows),
    responseCount: validation.rows.length,
    rows: rawRows.map(({ values }) => values),
  };
};
